// src/shared/ui/FormField.jsx
//
// Merges golive/components/ui/FormField.jsx and adai/components/ui/FormField.jsx
// (same label + required-asterisk + helper/error text + children-slot pattern,
// written twice with slightly different prop names). Superset of both APIs:
//   - `badge`   (golive: small pill next to the label, e.g. "Optional")
//   - `icon`    (adai: small lucide icon next to the label)
//   - `error` + `errorText` (golive: boolean flag + separate message)
//   - `error` as an object with `.message` (adai/react-hook-form style) also works.

import PropTypes from "prop-types";

export default function FormField({ label, required, badge, icon: Icon, helperText, error, errorText, children }) {
  const resolvedErrorText =
    errorText ?? (error && typeof error === "object" ? error.message : null) ?? (error ? "This field is required" : null);
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <div className="flex items-center gap-2">
        <label className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          {Icon && <Icon size={14} className="text-slate-400" />}
          {label}
          {required && <span className="text-[var(--jade-danger-700)] ml-0.5">*</span>}
        </label>
        {badge && (
          <span className="text-xs px-2 py-0.5 rounded-full border border-gray-300 text-gray-400">{badge}</span>
        )}
      </div>
      {children}
      {hasError && resolvedErrorText ? (
        <p className="text-[var(--jade-danger-700)] text-xs font-medium">{resolvedErrorText}</p>
      ) : (
        helperText && <p className="text-xs text-slate-400">{helperText}</p>
      )}
    </div>
  );
}

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  badge: PropTypes.string,
  icon: PropTypes.elementType,
  helperText: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  errorText: PropTypes.string,
  children: PropTypes.node.isRequired,
};
